import React from 'react';
import type { BOQHeader } from '@/services/boq.service';
import { EngineCommercialA4Print } from '@/components/print/EngineCommercialPrints';
import {
  buildBoqPrintLines,
  buildBoqPrintMetaLines,
  formatBoqPrintAmount,
} from '@/utils/boqPrint';

export interface PrintableBOQEstimationProps {
  boq: BOQHeader;
  onClose?: () => void;
}

/** BOQ estimation print — line summary with materials, operations, and cost breakdown per line. */
export const PrintableBOQEstimation: React.FC<PrintableBOQEstimationProps> = ({ boq, onClose }) => {
  const currency = boq.currency || 'AED';
  const lines = buildBoqPrintLines(boq);
  const costTotal = Number(boq.total_amount ?? 0);
  const sellTotal = Number(boq.selling_price ?? 0);
  const marginAmt = sellTotal - costTotal;

  return (
    <EngineCommercialA4Print
      docType="boq_estimation"
      title="BOQ ESTIMATION"
      documentNumber={boq.boq_number || 'BOQ'}
      partyName={boq.customer_name || boq.boq_name || ''}
      onClose={onClose}
      landscape
      metaLines={buildBoqPrintMetaLines(boq)}
      introText={
        boq.notes?.trim()
          ? `Notes: ${boq.notes.trim()}`
          : 'Detailed bill of quantities with materials and operations per line item.'
      }
      columns={[
        { key: 'sno', label: '#' },
        { key: 'description', label: 'Item' },
        { key: 'qty', label: 'Qty', align: 'right' },
        { key: 'uom', label: 'UOM' },
        { key: 'unitCost', label: 'Unit cost', align: 'right' },
        { key: 'unitSell', label: 'Unit sell', align: 'right' },
        { key: 'lineCost', label: 'Line cost', align: 'right' },
        { key: 'lineSell', label: 'Line sell', align: 'right' },
      ]}
      lines={lines}
      finals={[{ id: 'totals', heightPx: 64 }]}
      finalBlocks={{
        totals: (
          <div className="print-letter-totals">
            <table>
              <tbody>
                <tr>
                  <td>Total cost :</td>
                  <td>{formatBoqPrintAmount(costTotal, currency)}</td>
                </tr>
                <tr>
                  <td>Total markup :</td>
                  <td>{formatBoqPrintAmount(marginAmt, currency)}</td>
                </tr>
                <tr className="grand-total">
                  <td>Selling price :</td>
                  <td>{formatBoqPrintAmount(sellTotal, currency)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        ),
      }}
    />
  );
};
